'use client'

import {
  Activity,
  ArrowRight,
  Assembly as Blocks,
  Branch as GitBranch,
  ChartColumn as BarChart3,
  Cloud,
  DataBase as Database,
  Growth as TrendingUp,
  Lightning as Zap,
  Meter as Gauge,
  Renew as RefreshCw,
  Screen as MonitorDot,
  Security as Shield,
  BareMetalServer_02 as ServerCog,
  SettingsAdjust as Settings2,
  Terminal as TerminalIcon,
  View as Eye,
} from '@carbon/icons-react'
import Link from 'next/link'

import { Button } from '@/components/ui/button'
import { AnimatedNumber } from '@/components/ui/animated-number'
import { BrowserStack } from '@/components/ui/browser-stack'
import {
  AnimatedSpan,
  Line,
  Prompt,
  TERM,
  Terminal,
  TypingAnimation,
} from '@/components/ui/terminal'
import { ChromaText } from '@/components/ui/chroma-text'
import { HAIRLINE, PANEL, useReveal } from '@/components/page-kit'
import { cn } from '@/lib/utils'

/* ── Copy ─────────────────────────────────────────────────────────────
   Every string below is carried over verbatim from the previous version of
   this page. The redesign changes how it is presented, never what it says. */

const CAPABILITIES = [
  {
    key: 'cloud',
    icon: Cloud,
    label: 'Cloud Native',
    title: 'Elastic Cloud Infrastructure',
    body: 'Microservices architecture with containerisation and orchestration. Auto-scaling across availability zones ensures zero-downtime deployments even under extreme load.',
    features: ['Auto-scaling', 'Multi-AZ', 'Zero Downtime'],
  },
  {
    key: 'security',
    icon: Shield,
    label: 'Security',
    title: 'Zero-Trust Security',
    body: 'End-to-end encryption, OAuth 2.0, RBAC, and compliance with GDPR, SOC 2, and ISO 27001. Every request is authenticated, authorised, and audited.',
    features: ['E2E Encryption', 'RBAC', 'SOC 2'],
  },
  {
    key: 'performance',
    icon: Zap,
    label: 'Performance',
    title: 'Sub-50ms Response Times',
    body: 'Distributed caching, query optimisation, edge CDN and lazy loading deliver lightning-fast performance at every layer of the stack.',
    features: ['CDN Edge', 'Query Opt.', 'Smart Cache'],
  },
  {
    key: 'data',
    icon: Database,
    label: 'Data Layer',
    title: 'Unified Data Architecture',
    body: 'Polyglot persistence with PostgreSQL, Redis, and time-series databases. Real-time replication and automated backups with point-in-time recovery.',
    features: ['Polyglot DB', 'Replication', 'PITR'],
  },
  {
    key: 'integration',
    icon: RefreshCw,
    label: 'Integration',
    title: 'Open Integration Layer',
    body: 'RESTful and GraphQL APIs with OpenAPI specs, webhooks, event streaming, and 100+ pre-built connectors for enterprise systems.',
    features: ['REST/GraphQL', 'Streaming', '100+ Connectors'],
  },
  {
    key: 'observe',
    icon: Eye,
    label: 'Observability',
    title: 'Full-Stack Observability',
    body: 'Distributed tracing, structured logging, and real-time metrics dashboards. Intelligent alerting surfaces issues before users notice them.',
    features: ['Tracing', 'Structured Logs', 'Smart Alerts'],
  },
  {
    key: 'erp',
    icon: ServerCog,
    label: 'ERP',
    title: 'Unified ERP Modules',
    body: 'Integrated accounting, HR, payroll, and LMS with shared data models and cross-module workflows. One source of truth for all institutional operations.',
    features: ['Shared Model', 'Cross-module', 'Single Auth'],
  },
]

const PRINCIPLES = [
  {
    title: 'Security First',
    desc: 'Zero-trust with encryption at rest and in transit, everywhere, always.',
  },
  {
    title: 'Built to Scale',
    desc: 'Horizontal scaling from day one. Handle millions of transactions without re-architecture.',
  },
  {
    title: 'Full Observability',
    desc: 'Every request traced, every metric captured, every anomaly surfaced in real time.',
  },
  {
    title: 'Composable',
    desc: 'Modular services that compose freely. Swap, extend, or replace any layer.',
  },
  {
    title: 'Standards-Driven',
    desc: 'OpenAPI, OAuth 2.0, and OpenTelemetry baked in from the first commit.',
  },
]

const STATS = [
  { value: '99.9', label: 'Uptime SLA', icon: Gauge, suffix: '%' },
  { value: '50', label: 'ms Avg Latency', icon: Zap, suffix: 'ms' },
  { value: '24', label: '/7 Monitoring', icon: Activity, suffix: '' },
]

const STACK = [
  {
    category: 'Frontend',
    tech: 'React · Next.js · TypeScript · Angular',
    icon: MonitorDot,
  },
  {
    category: 'Backend',
    tech: 'Node.js · Python · Go · Java',
    icon: TerminalIcon,
  },
  {
    category: 'Database',
    tech: 'PostgreSQL · Redis · InfluxDB',
    icon: Database,
  },
  { category: 'Cloud', tech: 'AWS · Azure · GCP', icon: Cloud },
  { category: 'DevOps', tech: 'Kubernetes · Docker · Helm', icon: Settings2 },
  {
    category: 'Observability',
    tech: 'Prometheus · Grafana · Jaeger',
    icon: BarChart3,
  },
]

const LAYERS = [
  { label: 'Presentation', desc: 'React · Next.js · PWA' },
  { label: 'API Gateway', desc: 'GraphQL · REST · gRPC' },
  { label: 'Business Logic', desc: 'Node.js · Python · Go' },
  { label: 'Persistence', desc: 'PostgreSQL · Redis · S3' },
  { label: 'Infrastructure', desc: 'Kubernetes · Terraform' },
]

const DEPLOY_STEPS = [
  'Design a cloud environment tailored to your scale and compliance requirements',
  'Zero data-loss migration with full continuity and rollback at every stage',
  'All modules connected with seamless real-time data flow across the ecosystem',
  'Continuous observability dashboards with 24/7 live monitoring and alerting',
]

/* Terse stage labels for the deploy log. Each one names the step it maps to in
   DEPLOY_STEPS; the prose lives in the list beside the terminal, so the log
   stays a log instead of repeating the sentence. */
const DEPLOY_STAGES = [
  { name: 'provision', detail: 'cloud environment' },
  { name: 'migrate', detail: 'zero data-loss, rollback armed' },
  { name: 'connect', detail: 'realtime data flow' },
  { name: 'observe', detail: 'dashboards + 24/7 alerting' },
]

const PRINCIPLE_ICONS = [Shield, TrendingUp, Eye, GitBranch, Blocks]

const SHELL = 'font-mono text-[11px] tracking-tight'

/* Hairline grid. Each cell draws its own 1px outline into the gap, so adjacent
   cell carries its own right and bottom rule and the container closes the top
   and left. Painting the rule as a background under a gap-px grid instead —
   the pattern used elsewhere in this repo — also fills any cell the last row
   leaves empty, which reads as a stray grey block whenever the item count is
   not a multiple of the column count. */
/** An odd-length list leaves a lone cell in the last row. Letting that cell
    span the full width closes the grid as a rectangle instead. */
const spanIfOrphan = (i: number, len: number) =>
  len % 2 === 1 && i === len - 1 ? 'sm:col-span-2' : ''

const GRID_RULES =
  'grid overflow-hidden rounded-xl border-l border-t border-border/70 ' +
  '[&>*]:border-b [&>*]:border-r [&>*]:border-border/70'

/* ── Page device ──────────────────────────────────────────────────────
   Each section announces itself as a path typed at a prompt. It is the one
   motif that repeats down the page and it ties the prose to the consoles
   sitting beside it. */
function PathHead({
  path,
  title,
  deck,
  className,
}: {
  path: string
  title: React.ReactNode
  deck?: React.ReactNode
  className?: string
}) {
  return (
    <div className={className}>
      <p
        className={cn(SHELL, 'flex items-center gap-1.5 text-muted-foreground')}
      >
        <span aria-hidden className="text-emerald-600 dark:text-primary-active">
          $
        </span>
        <span>{path}</span>
      </p>

      <h2 className="font-heading mt-4 text-2xl font-normal leading-tight text-foreground sm:text-3xl md:text-4xl lg:text-[44px]">
        <ChromaText>{title}</ChromaText>
      </h2>

      {deck && (
        <p className="mt-4 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base md:text-[17px]">
          {deck}
        </p>
      )}
    </div>
  )
}

/** Section wrapper: full-bleed hairline above, consistent rhythm. */
function Section({
  children,
  className,
  id,
}: {
  children: React.ReactNode
  className?: string
  id?: string
}) {
  return (
    <section
      id={id}
      className={cn('border-t py-16 sm:py-20 lg:py-28', HAIRLINE, className)}
    >
      <div className="mx-auto w-full max-w-[1180px] px-6 lg:px-8">
        {children}
      </div>
    </section>
  )
}

/* ── Layer rack ───────────────────────────────────────────────────────
   The stack as a rack of units, read top-down. Hairlines and an index do the
   work that a row of gradient progress bars used to, which claimed a
   completeness percentage the copy never actually stated. */
function LayerRack() {
  const { ref, shown } = useReveal<HTMLDivElement>(0.25)

  return (
    <div ref={ref} className={cn('overflow-hidden rounded-xl', PANEL)}>
      <div
        className={cn(
          'flex items-center justify-between border-b px-4 py-2.5',
          HAIRLINE
        )}
      >
        <span
          className={cn(
            SHELL,
            'uppercase tracking-[0.18em] text-muted-foreground'
          )}
        >
          System Layers
        </span>
        <span className={cn(SHELL, 'text-muted-foreground/70')}>5 units</span>
      </div>

      <ol>
        {LAYERS.map((row, i) => (
          <li
            key={row.label}
            className={cn(
              'flex items-baseline gap-4 px-4 py-3.5 transition-all duration-500',
              i > 0 && cn('border-t', HAIRLINE),
              shown ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0'
            )}
            style={{ transitionDelay: `${i * 70}ms` }}
          >
            <span
              className={cn(SHELL, 'w-6 shrink-0 text-muted-foreground/60')}
            >
              {String(i + 1).padStart(2, '0')}
            </span>

            <span className="w-28 shrink-0 text-sm font-medium text-foreground sm:w-36">
              {row.label}
            </span>

            <span
              className={cn(
                SHELL,
                'min-w-0 flex-1 truncate text-muted-foreground'
              )}
            >
              {row.desc}
            </span>
          </li>
        ))}
      </ol>
    </div>
  )
}

/* ── Page ─────────────────────────────────────────────────────────── */
export default function ArchitecturePage() {
  return (
    <main className="bg-background">
      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden">
        {/* Circuit texture, faded out before the first hairline so the section
            boundary is a rule rather than a colour change. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.5] dark:opacity-40"
          style={{
            backgroundImage: 'var(--circuit-bg)',
            maskImage:
              'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.25) 45%, transparent 92%)',
            WebkitMaskImage:
              'linear-gradient(to bottom, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.25) 45%, transparent 92%)',
          }}
        />

        <div className="relative mx-auto w-full max-w-[1180px] px-6 pb-16 pt-14 sm:pb-20 sm:pt-20 lg:px-8 lg:pb-28 lg:pt-24">
          <div className="grid items-center gap-12 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:gap-16">
            <div>
              <p
                className={cn(
                  SHELL,
                  'flex items-center gap-1.5 text-muted-foreground'
                )}
              >
                <span
                  aria-hidden
                  className="text-emerald-600 dark:text-primary-active"
                >
                  $
                </span>
                <span>euclideum/architecture</span>
              </p>

              <h1 className="font-heading mt-5 text-[clamp(2.25rem,6vw,4rem)] font-normal leading-[1.06] tracking-tight text-foreground">
                <ChromaText>
                  Architecture that scales precisely with your ambition
                </ChromaText>
              </h1>

              <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">
                Enterprise-grade infrastructure engineered for high
                availability, ironclad security, and blazing performance. From
                day one to planet scale, built to support growing teams
                worldwide.
              </p>

              <div className="mt-8">
                <Button size="lg" className="font-medium" asChild>
                  <Link href="#capabilities">
                    Explore Architecture
                    <ArrowRight className="ml-2 size-4" />
                  </Link>
                </Button>
              </div>

              {/* Stats as a hairline strip rather than floating pills. */}
              <dl
                className={cn('mt-10 grid grid-cols-3 border-t pt-6', HAIRLINE)}
              >
                {STATS.map((s) => (
                  <div key={s.label} className="min-w-0">
                    <dt className="sr-only">{s.label}</dt>
                    <dd className="font-heading text-xl font-normal text-foreground sm:text-2xl">
                      <AnimatedNumber value={`${s.value}${s.suffix}`} />
                    </dd>
                    <p className="mt-1 truncate text-xs text-muted-foreground sm:text-[13px]">
                      {s.label}
                    </p>
                  </div>
                ))}
              </dl>
            </div>

            {/* The console. Its transcript is the page's own numbers, read back
                as a status check. */}
            <Terminal
              title="euclideum: core (zsh)"
              className="max-w-xl lg:justify-self-end"
            >
              <span>
                <Prompt dir="~/core" />
                <TypingAnimation className={TERM.cmd}>
                  euclideum status --cluster core
                </TypingAnimation>
              </span>
              <AnimatedSpan>
                <Line tone="ok" glyph="✔">
                  Preflight checks
                </Line>
              </AnimatedSpan>
              <AnimatedSpan>
                <Line tone="ok" glyph="✔">
                  48 services · 12 data nodes · 3 availability zones
                </Line>
              </AnimatedSpan>
              <AnimatedSpan>
                <Line tone="ok" glyph="✔">
                  Uptime SLA <span className={TERM.dim}>·············</span>{' '}
                  99.9%
                </Line>
              </AnimatedSpan>
              <AnimatedSpan>
                <Line tone="ok" glyph="✔">
                  Avg latency <span className={TERM.dim}>············</span>{' '}
                  50ms
                </Line>
              </AnimatedSpan>
              <AnimatedSpan>
                <Line tone="ok" glyph="✔">
                  Monitoring <span className={TERM.dim}>·············</span>{' '}
                  24/7
                </Line>
              </AnimatedSpan>
              <AnimatedSpan>
                <span className={TERM.ok}>All Systems Healthy</span>
              </AnimatedSpan>
              <AnimatedSpan>
                <span>
                  <Prompt dir="~/core" />
                  <span
                    aria-hidden
                    className="terminal-caret inline-block h-[1.05em] w-[0.5em] translate-y-[0.15em] bg-current align-baseline"
                  />
                </span>
              </AnimatedSpan>
            </Terminal>
          </div>
        </div>
      </section>

      {/* ── Overview ─────────────────────────────────────────────── */}
      <Section>
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          <PathHead
            path="~/core/overview"
            title="Modern architecture for modern challenges"
          />

          <div className="space-y-4">
            <p className="text-base leading-relaxed text-muted-foreground sm:text-[17px]">
              Founded on{' '}
              <span className="text-foreground">cloud-native principles</span>{' '}
              and{' '}
              <span className="text-foreground">industry best practices</span>.
              Containerised, event-driven, and composable from the ground up.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              Every layer is designed for resilience. Microservices, distributed
              caching, and intelligent auto-scaling deliver consistent
              performance regardless of load spikes.
            </p>
            <p className="text-base leading-relaxed text-muted-foreground">
              One platform. Every service. Zero compromise on security, scale,
              or speed.
            </p>
          </div>
        </div>

        <div className="mt-12 lg:mt-16">
          <LayerRack />
        </div>
      </Section>

      {/* ── Capabilities ─────────────────────────────────────────── */}
      <Section id="capabilities">
        <PathHead
          path="~/core/capabilities"
          title="What we build"
          deck="Seven capabilities, each one a layer of the same platform."
        />

        <ul className={cn('mt-12 sm:grid-cols-2', GRID_RULES)}>
          {CAPABILITIES.map((cap, i) => {
            const Icon = cap.icon
            return (
              <li
                key={cap.key}
                className={cn(
                  'group relative flex flex-col bg-background p-6 transition-colors duration-300 hover:bg-muted/50 sm:p-7 lg:p-8',
                  spanIfOrphan(i, CAPABILITIES.length)
                )}
              >
                <div className="flex items-center gap-3">
                  <Icon
                    size={20}
                    className="shrink-0 text-emerald-600 dark:text-primary-active"
                  />
                  <span
                    className={cn(
                      SHELL,
                      'uppercase tracking-[0.16em] text-muted-foreground'
                    )}
                  >
                    {String(i + 1).padStart(2, '0')} · {cap.label}
                  </span>
                </div>

                <h3 className="font-heading mt-4 text-lg font-normal leading-snug text-foreground">
                  {cap.title}
                </h3>

                <p className="mt-2.5 flex-1 text-sm leading-relaxed text-muted-foreground">
                  {cap.body}
                </p>

                <div
                  className={cn(
                    'mt-5 flex flex-wrap gap-x-3 gap-y-1.5 border-t pt-4',
                    HAIRLINE
                  )}
                >
                  {cap.features.map((f) => (
                    <span
                      key={f}
                      className={cn(SHELL, 'text-muted-foreground')}
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </li>
            )
          })}
        </ul>
      </Section>

      {/* ── Deploy ───────────────────────────────────────────────── */}
      <Section>
        <div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
          <Terminal title="euclideum: deploy (zsh)" className="lg:order-1">
            <span>
              <Prompt dir="~/deploy" />
              <TypingAnimation className={TERM.cmd}>
                euclideum deploy --env production
              </TypingAnimation>
            </span>
            <AnimatedSpan>
              <Line>
                {'  '}target{'    '}
                <span className={TERM.key}>cluster/core</span>
              </Line>
            </AnimatedSpan>
            <AnimatedSpan>
              <Line>
                {'  '}strategy{'  '}
                <span className={TERM.key}>zero-downtime</span>
              </Line>
            </AnimatedSpan>
            <AnimatedSpan>
              <Line> </Line>
            </AnimatedSpan>
            {/* Stage labels, not the prose beside it — the list already carries
                the full sentences, and a real deploy log is terse. */}
            {DEPLOY_STAGES.map((stage) => (
              <AnimatedSpan key={stage.name}>
                <Line tone="ok" glyph="✔">
                  <span className={TERM.key}>{stage.name.padEnd(10)}</span>
                  {stage.detail}
                </Line>
              </AnimatedSpan>
            ))}
            <AnimatedSpan>
              <Line> </Line>
            </AnimatedSpan>
            <AnimatedSpan>
              <span className={TERM.ok}>
                Production Ready · cluster.operational
              </span>
            </AnimatedSpan>
          </Terminal>

          <div className="lg:order-2">
            <PathHead
              path="~/core/deploy"
              title="How we deploy"
              deck="Engineering excellence with client-centric delivery"
            />

            <ol className={cn('mt-8 border-t', HAIRLINE)}>
              {DEPLOY_STEPS.map((step, i) => (
                <li
                  key={step}
                  className={cn('flex gap-4 border-b py-4', HAIRLINE)}
                >
                  <span
                    className={cn(
                      SHELL,
                      'mt-0.5 shrink-0 text-muted-foreground/60'
                    )}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {step}
                  </span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </Section>

      {/* ── Stack + multi-tenant ─────────────────────────────────── */}
      <Section>
        <div className="grid gap-12 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:gap-16">
          <div>
            <PathHead path="~/core/stack" title="Technology stack" />

            <ul className={cn('mt-8 border-t', HAIRLINE)}>
              {STACK.map((item) => {
                const Icon = item.icon
                return (
                  <li
                    key={item.category}
                    className={cn(
                      'flex items-center gap-4 border-b py-4 sm:gap-6',
                      HAIRLINE
                    )}
                  >
                    <Icon
                      size={18}
                      className="shrink-0 text-emerald-600 dark:text-primary-active"
                    />
                    <span className="w-28 shrink-0 text-sm font-medium text-foreground sm:w-36">
                      {item.category}
                    </span>
                    <span
                      className={cn(
                        SHELL,
                        'min-w-0 flex-1 text-muted-foreground'
                      )}
                    >
                      {item.tech}
                    </span>
                  </li>
                )
              })}
            </ul>
          </div>

          {/* The four modules named in the ERP capability, each its own front
              door onto the same core — a thing one window cannot show. */}
          <div className="flex flex-col">
            <PathHead
              path="~/core/modules"
              title="One core, every module"
              deck="Integrated accounting, HR, payroll, and LMS with shared data models and cross-module workflows. One source of truth for all institutional operations."
            />

            {/* The windows stand ON the section rule rather than floating above
                it: cancel the section's bottom padding and push the stack to
                the bottom edge, so the front window's base is the hairline. */}
            <div className="mt-10 flex flex-1 items-end justify-center -mb-16 sm:-mb-20 lg:mt-12 lg:-mb-28">
              <BrowserStack
                domains={[
                  'accounts.euclideum.com',
                  'hrms.euclideum.com',
                  'payroll.euclideum.com',
                  'lms.euclideum.com',
                ]}
              />
            </div>
          </div>
        </div>
      </Section>

      {/* ── Principles ───────────────────────────────────────────── */}
      <Section>
        <PathHead
          path="~/core/principles"
          title="Guided by our principles"
          deck="Six principles shape every architectural decision and every solution we build:"
        />

        <ul className={cn('mt-12 sm:grid-cols-2', GRID_RULES)}>
          {PRINCIPLES.map((p, i) => {
            const Icon = PRINCIPLE_ICONS[i]!
            return (
              <li
                key={p.title}
                className={cn(
                  'flex flex-col bg-background p-6 sm:p-7 lg:p-8',
                  spanIfOrphan(i, PRINCIPLES.length)
                )}
              >
                <Icon
                  size={20}
                  className="text-emerald-600 dark:text-primary-active"
                />
                <h3 className="font-heading mt-4 text-lg font-normal text-foreground">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {p.desc}
                </p>
              </li>
            )
          })}
        </ul>
      </Section>

      {/* ── CTA ──────────────────────────────────────────────────── */}
      <Section>
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <div>
            <PathHead
              path="~/core/contact"
              title="Ready to build something extraordinary?"
              deck="Let's discuss how our architecture can power your next enterprise solution. Join institutions that trust our infrastructure every day, at scale."
            />

            <div className="mt-8">
              <Button size="lg" className="font-medium" asChild>
                <Link href="/schedule-meeting">
                  Talk to our architects
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
            </div>
          </div>

          <blockquote className={cn('border-l py-2 pl-6 lg:pl-8', HAIRLINE)}>
            <p className="font-heading text-xl font-normal leading-snug text-foreground sm:text-2xl md:text-[28px]">
              &ldquo;Architecture is not about technology. It&rsquo;s about
              outcomes.&rdquo;
            </p>
          </blockquote>
        </div>
      </Section>
    </main>
  )
}
